/* eslint-disable max-len */
/**
 * @fileoverview Video Visual Studio: Compact single-panel generation.
 *               PATH: src/components/contentHub/VideoGenerationPanel.js
 *               Layout: Smart Auto card → Category pills → Vibe pills (preview on hover) → Audio toggle → Generate.
 *               Generates video once for all video-enabled platforms (FB/IG merge).
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from "firebase/functions";
import { functions } from '../../firebaseConfig';
import { useToast } from '../shared/contexts/ToastContext';
import {
    FiZap, FiLoader, FiPlay, FiMusic, FiVolumeX, FiVolume2
} from 'react-icons/fi';
import { VIDEO_TAXONOMY_MIRROR, SMART_AUTO_VIBE } from '../shared/constants/videoTaxonomyMirror';
import VibePreview from '../shared/VibePreview';

const generateVideoFromPromptFn = httpsCallable(functions, 'generateVideoFromPrompt', { timeout: 300000 });
const notifyAdminOfflineFn = httpsCallable(functions, 'notifyAdminOffline');

function VideoGenerationPanel({ project, post, onVisualUpdate, adjustments, activePlatform, workflowType, allPosts, onGenerateAllForPlatform, isGeneratingBatch }) {
    const { addToast } = useToast();

    // --- Configuration State ---
    const [isSmartAuto, setIsSmartAuto] = useState(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState(VIDEO_TAXONOMY_MIRROR[0].id);
    const [selectedVibe, setSelectedVibe] = useState(SMART_AUTO_VIBE);

    // --- Audio Mode ---
    const [audioMode, setAudioMode] = useState('auto');

    // --- Loading State ---
    const [isRendering, setIsRendering] = useState(false);
    const [renderStep, setRenderStep] = useState('');

    // --- UI State ---
    const [localError, setLocalError] = useState('');
    const [localOffline, setLocalOffline] = useState(false);
    const [notifyingAdmin, setNotifyingAdmin] = useState(false);
    const [hoveredVibeId, setHoveredVibeId] = useState(null);
    const [isSmartAutoHovered, setIsSmartAutoHovered] = useState(false);

    // --- Refs ---
    const lastContextRef = useRef({ postId: null, platform: null });

    // Effective categoryId for backend
    const effectiveCategoryId = isSmartAuto ? 'professional' : selectedCategoryId;
    const activeCategoryData = useMemo(() => VIDEO_TAXONOMY_MIRROR.find(c => c.id === selectedCategoryId) || VIDEO_TAXONOMY_MIRROR[0], [selectedCategoryId]);

    // --- Vibe Restoration ---
    useEffect(() => {
        const contextKey = `${post.id}-${activePlatform}`;
        const lastKey = `${lastContextRef.current.postId}-${lastContextRef.current.platform}`;
        if (contextKey === lastKey) return;
        lastContextRef.current = { postId: post.id, platform: activePlatform };

        const savedSettings = post.visualSettings;
        if (savedSettings?.vibeId) {
            const isAutoSaved = savedSettings.vibeId === 'smart_auto';
            setIsSmartAuto(isAutoSaved);
            setSelectedCategoryId(savedSettings.categoryId || VIDEO_TAXONOMY_MIRROR[0].id);
            if (isAutoSaved) {
                setSelectedVibe(SMART_AUTO_VIBE);
            } else {
                const cat = VIDEO_TAXONOMY_MIRROR.find(c => c.id === savedSettings.categoryId);
                if (cat) {
                    const vibe = cat.vibes.find(v => v.id === savedSettings.vibeId);
                    if (vibe) setSelectedVibe(vibe);
                }
            }
            if (savedSettings.audioMode && ['auto', 'background', 'no_audio'].includes(savedSettings.audioMode)) {
                setAudioMode(savedSettings.audioMode);
            }
        } else {
            setIsSmartAuto(true);
            setSelectedCategoryId(VIDEO_TAXONOMY_MIRROR[0].id);
            setSelectedVibe(SMART_AUTO_VIBE);
        }
    }, [post.id, activePlatform, post.visualSettings]);

    // ─── Generate Video ───────────────────────────────────────────────────
    const handleGenerateVideo = useCallback(async () => {
        setIsRendering(true);
        setLocalError('');
        setRenderStep('Generating 5s video... ~60 sec');

        try {
            const res = await generateVideoFromPromptFn({
                projectId: project.id,
                postId: post.id,
                platform: activePlatform,
                videoDuration: 5,
                settings: { categoryId: effectiveCategoryId, vibeId: selectedVibe.id },
                audioMode,
            });

            if (res.data?.success && res.data.visual) {
                onVisualUpdate(post.id, res.data.visual, activePlatform, res.data.newStatus);
                addToast('Video generated!', 'success');
            } else {
                throw new Error("Video generation failed.");
            }
        } catch (err) {
            const isOffline = err?.code === 'functions/unavailable' || err?.message?.includes('LOCAL_OFFLINE');
            if (isOffline) {
                setLocalOffline(true);
                setLocalError("Local machine is currently offline.");
                addToast("Local machine is offline.", 'error');
            } else {
                setLocalError("Video generation failed. Please try again.");
                addToast("Video generation failed.", 'error');
            }
        } finally {
            setIsRendering(false);
            setRenderStep('');
        }
    }, [project.id, post.id, activePlatform, effectiveCategoryId, selectedVibe, audioMode, onVisualUpdate, addToast]);

    return (
        <div className="p-5 bg-warm-gray/30 text-left space-y-4">

            {/* Error display */}
            {localError && (
                <div className="text-[11px] text-red-400 bg-red-500/10 p-2.5 rounded-xl border border-red-500/20 flex items-center justify-between">
                    <span className="font-bold">{localError}</span>
                    {localOffline && (
                        <button
                            type="button"
                            onClick={async () => {
                                setNotifyingAdmin(true);
                                try { await notifyAdminOfflineFn(); addToast('Admin notified.', 'success'); }
                                catch { addToast('Failed to notify.', 'error'); }
                                finally { setNotifyingAdmin(false); }
                            }}
                            disabled={notifyingAdmin}
                            className="ml-3 px-2 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 rounded text-[9px] font-bold uppercase transition-colors disabled:opacity-50"
                        >
                            {notifyingAdmin ? '...' : 'Notify Admin'}
                        </button>
                    )}
                </div>
            )}

            {/* ─── Video Style ─── */}
            <div>
                <label className="block text-[10px] font-bold text-cream/30 uppercase tracking-widest mb-2.5">Video Style</label>

                {/* Smart Auto — standalone card, visually distinct, preview on hover */}
                <div className="relative mb-3">
                    <div
                        onClick={() => { if (!isRendering) { setIsSmartAuto(true); setSelectedVibe(SMART_AUTO_VIBE); } }}
                        onMouseEnter={() => setIsSmartAutoHovered(true)}
                        onMouseLeave={() => setIsSmartAutoHovered(false)}
                        className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${isSmartAuto ? 'border-slate-blue bg-slate-blue/10' : 'border-white/10 bg-warm-gray/10 hover:border-slate-blue/30'} ${isRendering ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                        <div className="flex items-center gap-2">
                            <FiZap size={14} className={isSmartAuto ? 'text-slate-blue' : 'text-cream/40'} />
                            <span className={`text-sm font-black uppercase tracking-tight ${isSmartAuto ? 'text-slate-blue' : 'text-cream'}`}>Smart Auto</span>
                            <span className="text-[10px] text-cream/30 ml-auto">AI picks the best style</span>
                        </div>
                    </div>
                    {isSmartAutoHovered && (
                        <div className="absolute z-50 top-full left-0 mt-1 bg-warm-dark border border-white/10 rounded-xl shadow-2xl p-3 pointer-events-none" style={{ minWidth: '320px' }}>
                            <p className="text-[10px] text-cream/40 mb-2">{SMART_AUTO_VIBE.desc}</p>
                            <VibePreview vibeId="smart_auto" type="video" />
                        </div>
                    )}
                </div>

                {/* OR divider */}
                <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 border-t border-white/5"></div>
                    <span className="text-[9px] text-cream/20 uppercase tracking-widest font-bold">or choose a style</span>
                    <div className="flex-1 border-t border-white/5"></div>
                </div>

                {/* Category pills */}
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {VIDEO_TAXONOMY_MIRROR.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => { if (!isRendering) { setIsSmartAuto(false); setSelectedCategoryId(cat.id); if (isSmartAuto) setSelectedVibe(cat.vibes[0]); } }}
                            disabled={isRendering}
                            className={`px-3 py-2 text-[11px] font-bold rounded-xl transition-all ${!isSmartAuto && selectedCategoryId === cat.id ? 'bg-slate-blue text-warm-dark' : 'bg-warm-gray/20 text-cream/60 border border-white/10 hover:border-slate-blue/30'} disabled:opacity-50`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Vibe pills for selected category */}
                {!isSmartAuto && (
                    <div>
                        <label className="block text-[9px] font-bold text-cream/20 uppercase tracking-widest mb-2">{activeCategoryData.label} Vibes</label>
                        <div className="flex flex-wrap gap-1.5">
                            {activeCategoryData.vibes.map(vibe => (
                                <div key={vibe.id} className="relative">
                                    <button
                                        onClick={() => { if (!isRendering) { setSelectedVibe(vibe); } }}
                                        onMouseEnter={() => setHoveredVibeId(vibe.id)}
                                        onMouseLeave={() => setHoveredVibeId(null)}
                                        disabled={isRendering}
                                        className={`px-3 py-2 text-[11px] font-bold rounded-xl transition-all ${selectedVibe.id === vibe.id ? 'bg-slate-blue/20 text-slate-blue border border-slate-blue/40' : 'bg-warm-gray/10 text-cream/50 border border-white/5 hover:border-slate-blue/30 hover:text-cream'} disabled:opacity-50`}
                                    >
                                        {vibe.label}
                                    </button>

                                    {/* Preview popup on hover */}
                                    {hoveredVibeId === vibe.id && (
                                        <div className="absolute z-50 bottom-full left-0 mb-2 bg-warm-dark border border-white/10 rounded-xl shadow-2xl p-3 pointer-events-none" style={{ minWidth: '320px' }}>
                                            <p className="text-[10px] text-cream/40 mb-2 leading-tight">{vibe.desc}</p>
                                            <VibePreview vibeId={vibe.id} type="video" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ─── Audio Toggle ─── */}
            <div>
                <label className="block text-[10px] font-bold text-cream/30 uppercase tracking-widest mb-2">Audio</label>
                <div className="flex rounded-xl border border-white/10 overflow-hidden">
                    {[
                        { id: 'auto', label: 'Auto', desc: 'Speech + sounds', icon: <FiVolume2 size={13} /> },
                        { id: 'background', label: 'Background', desc: 'Sounds only', icon: <FiMusic size={13} /> },
                        { id: 'no_audio', label: 'No Audio', desc: 'Silent', icon: <FiVolumeX size={13} /> },
                    ].map(mode => (
                        <button
                            key={mode.id}
                            onClick={() => { if (!isRendering) setAudioMode(mode.id); }}
                            disabled={isRendering}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-[11px] font-bold transition-all ${audioMode === mode.id ? 'bg-slate-blue text-warm-dark' : 'bg-warm-gray/20 text-cream/50 hover:text-cream hover:bg-warm-gray/30'} disabled:opacity-50`}
                            title={mode.desc}
                        >
                            {mode.icon}
                            {mode.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ─── Generate Video ─── */}
            <button
                onClick={handleGenerateVideo}
                disabled={isRendering || isGeneratingBatch}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-slate-blue hover:bg-slate-blue/80 text-cream border border-slate-blue rounded-xl text-sm font-black uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {isRendering ? <FiLoader className="animate-spin" size={16} /> : <FiZap size={16} />}
                {isRendering ? renderStep : 'Generate Video'}
            </button>

            <p className="text-[9px] text-cream/25 text-center">All platforms receive the same video</p>

            {/* Generate All - Stream Mode */}
            {workflowType === 'stream' && allPosts && allPosts.length > 1 && (
                <button
                    onClick={() => onGenerateAllForPlatform(activePlatform)}
                    disabled={isRendering || isGeneratingBatch}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-warm-gray/20 hover:bg-warm-gray/30 text-cream/50 border border-white/10 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                >
                    <FiPlay size={13} />
                    Generate All Videos (auto)
                </button>
            )}
        </div>
    );
}

VideoGenerationPanel.propTypes = {
    project: PropTypes.object.isRequired,
    post: PropTypes.object.isRequired,
    onVisualUpdate: PropTypes.func.isRequired,
    adjustments: PropTypes.object,
    activePlatform: PropTypes.string,
    workflowType: PropTypes.string,
    allPosts: PropTypes.array,
    onGenerateAllForPlatform: PropTypes.func,
    isGeneratingBatch: PropTypes.bool,
};

export default VideoGenerationPanel;
